// Validator rules for the source-paper profile (Leah's Paper 1 engine spec,
// 15 October 2026, §3, §5 and §13). Pure. The run-level validator calls
// `sourcePaperIssues` for blocks and warnings and `sourcePaperProseFields` for
// the register rules.
//
// A block is either a break in the fixed format (the spec says those are the
// engine's, never the practice's) or a fault that would silently mis-check a
// learner: a pattern that does not compile, or one that matches an empty
// answer and so marks every answer "in place". The bank sizes in spec §5 are
// targets ("about five"), so they warn.

import type { ReviseIssue } from '../validate';
import {
  SOURCE_PAPER_FORMAT,
  SOURCE_PAPER_ROLES,
  type SourcePaperBlock,
  type SourcePaperByVariant,
  type SourcePaperOpvlBank,
  type SourcePaperRevisionCard,
  type SourcePaperSourceBody,
} from '../types';

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const strList = (v: unknown): v is string[] => Array.isArray(v) && v.length > 0 && v.every(str);
const isByVariant = (v: unknown): v is { byVariant: Record<string, unknown> } => isObj(v) && 'byVariant' in v;

/** Why a check pattern cannot be used, or null when it can. */
export function patternProblem(p: unknown): string | null {
  if (!str(p)) return 'is empty';
  let rx: RegExp;
  try {
    rx = new RegExp(p, 'i');
  } catch (e) {
    return `does not compile (${(e as Error).message})`;
  }
  if (rx.test('')) return 'matches an empty answer, so it would mark every answer as in place';
  return null;
}

/** Every block and warning a source-paper block earns. No blocks means it is playable. */
export function sourcePaperIssues(p: SourcePaperBlock, imageIds: ReadonlySet<string>): ReviseIssue[] {
  const out: ReviseIssue[] = [];
  const block = (code: string, message: string) => out.push({ code, severity: 'block', message });
  const warn = (code: string, message: string) => out.push({ code, severity: 'warn', message });
  const pattern = (where: string, v: unknown) => {
    const problem = patternProblem(v);
    if (problem) block('sourcePaper.pattern', `${where} ${problem}.`);
  };
  /** The object entries of a list; anything else is blocked here, so later reads never touch a null. */
  const objects = <T,>(where: string, list: unknown): T[] => {
    if (!Array.isArray(list)) return [];
    list.forEach((x, i) => { if (!isObj(x)) block('sourcePaper.shape', `${where}[${i}] is not an object.`); });
    return list.filter(isObj) as T[];
  };
  const uniqueIds = (where: string, items: Array<{ id?: unknown }>) => {
    const seen = new Set<string>();
    items.forEach((it, i) => {
      if (!str(it?.id)) block('sourcePaper.id', `${where}[${i}] has no id.`);
      else if (seen.has(it.id)) block('sourcePaper.id', `${where} has the id "${it.id}" twice.`);
      else seen.add(it.id);
    });
  };

  // --- The start screen -------------------------------------------------------
  if (!str(p.id) || !/^[a-z0-9_]+$/.test(p.id)) block('sourcePaper.id', 'sourcePaper.id is required: lower case, digits and underscores (it names the download file).');
  for (const k of ['title', 'titleAccent', 'brandline', 'intro', 'caseStudy'] as const) {
    if (!str(p[k])) block('sourcePaper.start', `sourcePaper.${k} is required.`);
  }
  if (!Array.isArray(p.how) || p.how.length === 0 || !p.how.every((h) => isObj(h) && str(h.head) && str(h.text))) block('sourcePaper.how', 'sourcePaper.how needs at least one card with head and text.');

  // --- Sources and variants ---------------------------------------------------
  const sources = isObj(p.sources) ? p.sources : {};
  const letters = Object.keys(sources);
  if (letters.length !== SOURCE_PAPER_FORMAT.sources) block('sourcePaper.sources', `A Paper 1 practice has ${SOURCE_PAPER_FORMAT.sources} sources (got ${letters.length}).`);
  for (const k of letters) if (!/^[A-Z]$/.test(k)) block('sourcePaper.sources', `Source key "${k}" must be one capital letter.`);

  const variantLetters = letters.filter((k) => isObj(sources[k]) && 'variants' in (sources[k] as object));
  if (variantLetters.length > 1) block('sourcePaper.variants', `Only one source may have variants (${variantLetters.join(', ')} do).`);
  const variantLetter = variantLetters[0] ?? null;
  const variantKeys = variantLetter ? Object.keys((sources[variantLetter] as { variants: object }).variants ?? {}) : [];
  if (variantLetter && variantKeys.length < 2) block('sourcePaper.variants', `Source ${variantLetter} has variants, so it needs at least two.`);
  if (variantLetter) {
    if (!str(p.defaultVariant) || !variantKeys.includes(p.defaultVariant)) block('sourcePaper.defaultVariant', `sourcePaper.defaultVariant must be one of ${variantKeys.join(', ')}.`);
  } else if (p.defaultVariant !== undefined) {
    block('sourcePaper.defaultVariant', 'sourcePaper.defaultVariant is set, but no source has variants.');
  }

  /** A by-variant value: one value, or exactly one per version. */
  const byVariant = <T>(where: string, v: SourcePaperByVariant<T> | undefined, each: (x: T, where: string) => void) => {
    if (v === undefined || v === null) {
      block('sourcePaper.missing', `${where} is required.`);
      return;
    }
    if (isByVariant(v)) {
      const keys = Object.keys(v.byVariant ?? {});
      if (!variantLetter) block('sourcePaper.variants', `${where} is written per version, but no source has variants.`);
      else if (keys.slice().sort().join() !== variantKeys.slice().sort().join()) block('sourcePaper.variants', `${where} must have exactly one entry per version (${variantKeys.join(', ')}); it has ${keys.join(', ') || 'none'}.`);
      for (const k of keys) each(v.byVariant[k] as T, `${where}.${k}`);
    } else each(v as T, where);
  };

  const bodies: Array<{ where: string; letter: string; body: SourcePaperSourceBody }> = [];
  for (const k of letters) {
    const s = sources[k];
    if (!isObj(s)) { block('sourcePaper.sources', `Source ${k} is not an object.`); continue; }
    if ('variants' in s) {
      for (const [vk, v] of Object.entries((s.variants ?? {}) as Record<string, unknown>)) {
        if (!isObj(v)) { block('sourcePaper.sources', `Source ${k} variant "${vk}" is not an object.`); continue; }
        if (!str(v.label)) block('sourcePaper.variants', `Source ${k} variant "${vk}" needs a label for the start screen.`);
        if (!str(v.short)) block('sourcePaper.variants', `Source ${k} variant "${vk}" needs a short line for the download heading.`);
        bodies.push({ where: `Source ${k} (${vk})`, letter: k, body: v as unknown as SourcePaperSourceBody });
      }
    } else bodies.push({ where: `Source ${k}`, letter: k, body: s as unknown as SourcePaperSourceBody });
  }
  const typeOf = (letter: string) => new Set(bodies.filter((b) => b.letter === letter).map((b) => b.body.type));
  for (const { where, body } of bodies) {
    if (!str(body.who)) block('sourcePaper.sources', `${where} needs its attribution (who).`);
    if (body.type === 'text') {
      if (!strList(body.paragraphs)) block('sourcePaper.sources', `${where} is a text source with no paragraphs.`);
    } else if (body.type === 'image') {
      if (!str(body.imageId)) block('sourcePaper.sources', `${where} is an image source with no imageId.`);
      else if (!imageIds.has(body.imageId)) block('ref.image', `${where} references image "${body.imageId}", which is not in run.images.`);
    } else block('sourcePaper.sources', `${where} has type ${JSON.stringify(body.type)}; it must be text or image.`);
    if (!body.copyright) warn('sourcePaper.copyright', `${where} has no copyright record. Record the status (and the author's date of death) before the practice is released.`);
    else if (body.copyright.status === 'flagged') warn('sourcePaper.copyright', `${where} is copyright-flagged${body.copyright.note ? `: ${body.copyright.note}` : '.'}`);
  }

  // --- Questions --------------------------------------------------------------
  const questions = objects<SourcePaperBlock['questions'][number]>('questions', p.questions);
  uniqueIds('questions', questions);
  if (questions.map((q) => q?.role).join() !== SOURCE_PAPER_ROLES.join()) {
    block('sourcePaper.questions', `A Paper 1 practice has five questions, one per role, in this order: ${SOURCE_PAPER_ROLES.join(', ')}.`);
  }
  const questionIds = new Set<string>();
  let marks = 0;
  for (const q of questions) {
    if (!isObj(q) || !(SOURCE_PAPER_ROLES as readonly string[]).includes(q.role as string)) continue;
    questionIds.add(q.id);
    const rule = SOURCE_PAPER_FORMAT.roles[q.role];
    const where = `Question ${q.n ?? q.id}`;
    if (!str(q.n)) block('sourcePaper.questions', `Question "${q.id}" needs its printed number (n).`);
    if (q.marks !== rule.marks) block('sourcePaper.marks', `${where} (${q.role}) is worth ${rule.marks} marks in Paper 1 (got ${q.marks}).`);
    marks += typeof q.marks === 'number' ? q.marks : 0;
    const used = Array.isArray(q.sources) ? q.sources : [];
    if (used.length !== rule.sources) block('sourcePaper.questions', `${where} (${q.role}) uses ${rule.sources} source${rule.sources === 1 ? '' : 's'} (got ${used.length}).`);
    for (const l of used) if (!letters.includes(l)) block('ref.source', `${where} uses Source ${l}, which is not in sourcePaper.sources.`);
    if (q.role === 'extract' && used[0] && !typeOf(used[0]).has('text')) block('sourcePaper.questions', `${where} asks what a written source says, but Source ${used[0]} is not a text source.`);
    if (q.role === 'visual' && used[0] && !typeOf(used[0]).has('image')) block('sourcePaper.questions', `${where} asks what a visual source suggests, but Source ${used[0]} is not an image source.`);
    byVariant(`${where} text`, q.text, (t, w) => { if (!str(t)) block('sourcePaper.questions', `${w} is empty.`); });
  }
  if (questions.length === SOURCE_PAPER_ROLES.length && marks !== SOURCE_PAPER_FORMAT.totalMarks) block('sourcePaper.marks', `The questions total ${marks} marks; Paper 1 is out of ${SOURCE_PAPER_FORMAT.totalMarks}.`);
  const byRole = (role: string) => questions.find((q) => q?.role === role);
  const opvlSource = byRole('opvl')?.sources?.[0];
  const compareSources = byRole('compare')?.sources ?? [];
  if (compareSources.length === 2 && compareSources[0] === compareSources[1]) block('sourcePaper.questions', 'The compare question must use two different sources.');

  // --- Extract and visual: points to pick ----------------------------------------
  for (const role of ['extract', 'visual'] as const) {
    const bank = p[role];
    const where = `sourcePaper.${role}`;
    if (!isObj(bank) || !Array.isArray(bank.items)) { block('sourcePaper.bank', `${where}.items is required.`); continue; }
    const items = objects<SourcePaperBlock['extract']['items'][number]>(`${where}.items`, bank.items);
    uniqueIds(`${where}.items`, items);
    items.forEach((it, i) => {
      if (!str(it.t) || !str(it.why)) block('sourcePaper.bank', `${where}.items[${i}] needs t and why.`);
      if (typeof it.ok !== 'boolean') block('sourcePaper.bank', `${where}.items[${i}] needs ok (true when the source makes the point).`);
      if (it.ok) pattern(`${where}.items[${i}].kw`, it.kw);
    });
    pattern(`${where}.outsideKw`, bank.outsideKw);
    pattern(`${where}.evidenceKw`, bank.evidenceKw);
    if (!Number.isInteger(bank.maxWords) || bank.maxWords <= 0) block('sourcePaper.bank', `${where}.maxWords must be a whole number above 0.`);
    const ok = items.filter((x) => x.ok).length;
    const wrong = items.length - ok;
    const need = SOURCE_PAPER_FORMAT.roles[role].marks;
    if (ok < need) block('sourcePaper.bank', `${where} has ${ok} points the source makes; the question is worth ${need}, so a learner could never pick enough.`);
    if (wrong === 0) block('sourcePaper.bank', `${where} has no wrong statements, so there is nothing to tell apart.`);
    // Spec §5: extract "about five" right and four wrong; visual "four or five" right and three wrong.
    const target = role === 'extract' ? { ok: [4, 6], wrong: [3, 5], says: 'about five right and four wrong' } : { ok: [4, 5], wrong: [3, 3], says: 'four or five right and three wrong' };
    if (ok < target.ok[0] || ok > target.ok[1] || wrong < target.wrong[0] || wrong > target.wrong[1]) {
      warn('sourcePaper.bankSize', `${where} has ${ok} right and ${wrong} wrong statements; the spec aims for ${target.says}.`);
    }
  }

  // --- Opvl: value and limitations -----------------------------------------------
  const opvlVaries = !!variantLetter && opvlSource === variantLetter;
  if (opvlVaries && !isByVariant(p.opvl)) block('sourcePaper.opvl', `Source ${variantLetter} has variants, so sourcePaper.opvl must be written per version.`);
  byVariant('sourcePaper.opvl', p.opvl, (bank, where) => {
    if (!isObj(bank) || !Array.isArray(bank.items)) { block('sourcePaper.opvl', `${where}.items is required.`); return; }
    const items = objects<SourcePaperOpvlBank['items'][number]>(`${where}.items`, bank.items);
    uniqueIds(`${where}.items`, items);
    items.forEach((it, i) => {
      if (!['Origin', 'Purpose', 'Content'].includes(it.c)) block('sourcePaper.opvl', `${where}.items[${i}].c must be Origin, Purpose or Content.`);
      if (it.ans !== 'V' && it.ans !== 'L') block('sourcePaper.opvl', `${where}.items[${i}].ans must be V or L.`);
      if (!str(it.t) || !str(it.why)) block('sourcePaper.opvl', `${where}.items[${i}] needs t and why.`);
    });
    for (const c of ['Origin', 'Purpose', 'Content']) {
      if (!items.some((it) => it.c === c)) block('sourcePaper.opvl', `${where} has no ${c} statement; full marks need a reference to each of origin, purpose and content.`);
    }
    for (const a of ['V', 'L'] as const) {
      if (!items.some((it) => it.ans === a)) block('sourcePaper.opvl', `${where} has no ${a === 'V' ? 'value' : 'limitation'}; full marks need both.`);
    }
    if (!isObj(bank.kw)) block('sourcePaper.opvl', `${where}.kw (origin and content patterns) is required.`);
    else {
      pattern(`${where}.kw.origin`, bank.kw.origin);
      pattern(`${where}.kw.content`, bank.kw.content);
      if (bank.kw.purpose !== undefined) pattern(`${where}.kw.purpose`, bank.kw.purpose);
      if (bank.kw.focus !== undefined) pattern(`${where}.kw.focus`, bank.kw.focus);
    }
    if (items.length < 8 || items.length > 10) warn('sourcePaper.bankSize', `${where} has ${items.length} statements; the spec aims for about nine.`);
  });

  // --- Compare -----------------------------------------------------------------
  const cmp = isObj(p.compare) && Array.isArray(p.compare.items) ? objects<SourcePaperBlock['compare']['items'][number]>('compare.items', p.compare.items) : null;
  if (!cmp) block('sourcePaper.compare', 'sourcePaper.compare.items is required.');
  else {
    uniqueIds('sourcePaper.compare.items', cmp);
    cmp.forEach((it, i) => {
      if (!['S', 'D', 'N'].includes(it.ans)) block('sourcePaper.compare', `compare.items[${i}].ans must be S, D or N.`);
      if (!str(it.t) || !str(it.why)) block('sourcePaper.compare', `compare.items[${i}] needs t and why.`);
      if (it.ans === 'S' || it.ans === 'D') pattern(`compare.items[${i}].kw`, it.kw);
    });
    const count = (a: string) => cmp.filter((x) => x.ans === a).length;
    for (const [a, name] of [['S', 'similarity'], ['D', 'difference'], ['N', 'not-a-comparison statement']] as const) {
      if (count(a) === 0) block('sourcePaper.compare', `sourcePaper.compare has no ${name}.`);
    }
    if (count('S') !== 3 || count('D') !== 4 || count('N') !== 2) warn('sourcePaper.bankSize', `sourcePaper.compare has ${count('S')} similarities, ${count('D')} differences and ${count('N')} non-comparisons; the spec aims for 3, 4 and 2.`);
  }

  // --- Essay ---------------------------------------------------------------------
  const e = p.essay;
  if (!isObj(e)) block('sourcePaper.essay', 'sourcePaper.essay is required.');
  else {
    for (const l of letters) {
      const st = isObj(e.stance) ? e.stance[l] : undefined;
      if (l === variantLetter && st !== undefined && !isByVariant(st)) block('sourcePaper.essay', `Source ${l} has variants, so its stance must be written per version.`);
      byVariant(`essay.stance.${l}`, st, (t, w) => { if (!str(t)) block('sourcePaper.essay', `${w} is empty.`); });
    }
    const ok = objects<SourcePaperBlock['essay']['ownKnowledge'][number]>('essay.ownKnowledge', e.ownKnowledge);
    uniqueIds('essay.ownKnowledge', ok);
    ok.forEach((f, i) => {
      if (!str(f.t) || !str(f.note)) block('sourcePaper.essay', `essay.ownKnowledge[${i}] needs t and note.`);
      pattern(`essay.ownKnowledge[${i}].kw`, f.kw);
    });
    if (ok.length < 3) block('sourcePaper.essay', `essay.ownKnowledge has ${ok.length} facts; the essay check looks for at least 3.`);
    else if (ok.length < 8 || ok.length > 12) warn('sourcePaper.bankSize', `essay.ownKnowledge has ${ok.length} facts; the spec aims for about ten.`);
    const persp = objects<SourcePaperBlock['essay']['perspectives'][number]>('essay.perspectives', e.perspectives);
    uniqueIds('essay.perspectives', persp);
    persp.forEach((x, i) => {
      if (!str(x.t) || !str(x.d) || !str(x.use)) block('sourcePaper.essay', `essay.perspectives[${i}] needs t, d and use.`);
      if (x.kw !== undefined) pattern(`essay.perspectives[${i}].kw`, x.kw);
    });
    if (persp.length === 0) block('sourcePaper.essay', 'essay.perspectives is empty; the argument step asks the learner to choose one.');
    else if (persp.length !== 3) warn('sourcePaper.bankSize', `essay.perspectives has ${persp.length}; the spec has three.`);
    const pos = objects<SourcePaperBlock['essay']['positions'][number]>('essay.positions', e.positions);
    uniqueIds('essay.positions', pos);
    pos.forEach((x, i) => { if (!str(x.t) || !str(x.d) || !str(x.line)) block('sourcePaper.essay', `essay.positions[${i}] needs t, d and line.`); });
    if (pos.length < 2) block('sourcePaper.essay', 'essay.positions needs at least two positions to choose between.');
    else if (pos.length !== 3) warn('sourcePaper.bankSize', `essay.positions has ${pos.length}; the spec has three.`);
    if (!strList(e.shape)) block('sourcePaper.essay', 'essay.shape (the shape you can use) is required.');
    if (!str(e.loadedWord)) block('sourcePaper.essay', 'essay.loadedWord is required.');
  }

  // --- Indicative content --------------------------------------------------------
  for (const q of questions) {
    if (!isObj(q) || !str(q.id)) continue;
    const ind = isObj(p.indicative) ? p.indicative[q.id] : undefined;
    if (q.role === 'opvl' && opvlVaries && ind !== undefined && !isByVariant(ind)) block('sourcePaper.indicative', `indicative.${q.id} must be written per version, since Source ${variantLetter} has variants.`);
    byVariant(`indicative.${q.id}`, ind, (list, w) => { if (!strList(list)) block('sourcePaper.indicative', `${w} needs at least one point.`); });
  }
  for (const k of Object.keys(isObj(p.indicative) ? p.indicative : {})) {
    if (!questionIds.has(k)) block('sourcePaper.indicative', `indicative.${k} is not a question id.`);
  }

  // --- Revision --------------------------------------------------------------------
  const rv = p.revise;
  if (!isObj(rv) || !str(rv.title) || !str(rv.blurb) || !Array.isArray(rv.cards) || rv.cards.length === 0) block('sourcePaper.revise', 'sourcePaper.revise needs title, blurb and at least one card.');
  else {
    for (const issue of revisionCardIssues(rv.cards, 'revise.cards')) out.push(issue);
    if (rv.cards.length < 5 || rv.cards.length > 7) warn('sourcePaper.bankSize', `revise.cards has ${rv.cards.length} topics; the spec aims for about six.`);
  }

  return out;
}

/** The card rules, shared with the engine-level skills pack (its test runs them too). */
export function revisionCardIssues(cards: SourcePaperRevisionCard[], where: string): ReviseIssue[] {
  const out: ReviseIssue[] = [];
  const block = (message: string) => out.push({ code: 'sourcePaper.revise', severity: 'block', message });
  cards.forEach((c, i) => {
    const w = `${where}[${i}]`;
    if (!isObj(c) || !str(c.title)) { block(`${w} needs a title.`); return; }
    if (!strList(c.cues) || !strList(c.points)) block(`${w} ("${c.title}") needs cues and points.`);
    else if (c.cues.length !== c.points.length) block(`${w} ("${c.title}") has ${c.cues.length} cues and ${c.points.length} points; every cue needs its point.`);
    if (c.types !== undefined && !Array.isArray(c.types)) block(`${w}.types must be a list of {type, values, limitations}.`);
    (Array.isArray(c.types) ? c.types : []).forEach((t, j) => {
      if (!isObj(t) || !str(t.type) || !str(t.values) || !str(t.limitations)) block(`${w}.types[${j}] needs type, values and limitations.`);
    });
  });
  return out;
}

/** Every string a learner reads that the practice wrote, for the register rules. Source text is quoted material and is left out. */
export function sourcePaperProseFields(p: SourcePaperBlock): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const add = (path: string, v: unknown) => {
    if (typeof v === 'string' && v.trim()) out.push([path, v]);
  };
  // Shape-safe reads: this runs on whatever an author sent, before or beside the blocks.
  const list = (v: unknown): any[] => (Array.isArray(v) ? v : []);
  const rec = (v: unknown): Record<string, any> => (isObj(v) ? v : {});
  const addV = (path: string, v: unknown, each: (x: unknown, path: string) => void) => {
    if (isByVariant(v)) for (const [k, x] of Object.entries(rec(v.byVariant))) each(x, `${path}.${k}`);
    else each(v, path);
  };
  for (const k of ['title', 'titleAccent', 'brandline', 'intro', 'caseStudy'] as const) add(k, p[k]);
  list(p.how).forEach((h, i) => { add(`how[${i}].head`, h?.head); add(`how[${i}].text`, h?.text); });
  for (const [l, s] of Object.entries(rec(p.sources))) {
    if (isObj(s) && 'variants' in s) for (const [k, v] of Object.entries(rec(s.variants))) { add(`sources.${l}.${k}.label`, v?.label); add(`sources.${l}.${k}.gloss`, v?.gloss); }
    else add(`sources.${l}.gloss`, (s as SourcePaperSourceBody | null)?.gloss);
  }
  list(p.questions).forEach((q) => addV(`questions.${q?.id}.text`, q?.text, (t, path) => add(path, t)));
  for (const role of ['extract', 'visual'] as const) {
    list(p[role]?.items).forEach((it, i) => { add(`${role}.items[${i}].t`, it?.t); add(`${role}.items[${i}].why`, it?.why); });
    add(`${role}.howto`, p[role]?.howto);
    add(`${role}.evidenceHint`, p[role]?.evidenceHint);
  }
  addV('opvl', p.opvl, (bank, path) => list((bank as { items?: unknown } | null)?.items).forEach((it, i) => { add(`${path}.items[${i}].t`, it?.t); add(`${path}.items[${i}].why`, it?.why); }));
  list(p.compare?.items).forEach((it, i) => { add(`compare.items[${i}].t`, it?.t); add(`compare.items[${i}].why`, it?.why); });
  const e = isObj(p.essay) ? p.essay : undefined;
  for (const [l, st] of Object.entries(rec(e?.stance))) addV(`essay.stance.${l}`, st, (t, path) => add(path, t));
  list(e?.ownKnowledge).forEach((f, i) => { add(`essay.ownKnowledge[${i}].t`, f?.t); add(`essay.ownKnowledge[${i}].note`, f?.note); });
  list(e?.perspectives).forEach((x, i) => { add(`essay.perspectives[${i}].t`, x?.t); add(`essay.perspectives[${i}].d`, x?.d); add(`essay.perspectives[${i}].use`, x?.use); });
  list(e?.positions).forEach((x, i) => { add(`essay.positions[${i}].t`, x?.t); add(`essay.positions[${i}].d`, x?.d); add(`essay.positions[${i}].line`, x?.line); });
  list(e?.shape).forEach((t, i) => add(`essay.shape[${i}]`, t));
  for (const [k, v] of Object.entries(rec(p.indicative))) addV(`indicative.${k}`, v, (items, path) => list(items).forEach((t, i) => add(`${path}[${i}]`, t)));
  add('revise.title', p.revise?.title);
  add('revise.blurb', p.revise?.blurb);
  list(p.revise?.cards).forEach((c, i) => {
    add(`revise.cards[${i}].title`, c?.title);
    list(c?.cues).forEach((t, j) => add(`revise.cards[${i}].cues[${j}]`, t));
    list(c?.points).forEach((t, j) => add(`revise.cards[${i}].points[${j}]`, t));
    list(c?.types).forEach((t, j) => {
      add(`revise.cards[${i}].types[${j}].type`, t?.type);
      add(`revise.cards[${i}].types[${j}].values`, t?.values);
      add(`revise.cards[${i}].types[${j}].limitations`, t?.limitations);
    });
  });
  return out;
}
